import React, { useEffect, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { DefaultTitle } from '@/se/components/collection/CollectionList';
import TabNavigation from '../sensor/TabNavigation';
import styled from 'styled-components';
import responsive from '@/se/utilities/responsive';
import { Header } from '../sensor/SensorPage';
import BlockSchedule from './BlockSchedule';
import { roundValue } from '../Analytics';
import BookingAnalytics from './BookingAnalytics';
import PhysicianPerformance from './PhysicianPerformance';
import OpenBlockAnalytics from './OpenBlockAnalytics';
import ActionBar from './ActionBar';
import RouterLink from '@/se/components/RouterLink';
import { withSession } from '@/state/Session';
import { unpackSessionObject } from '../unpackSessionObject';
import { compose } from 'recompose';
import { useSubscription } from '@apollo/client';
import { bookingStats } from '@/graph/bookings';
import { endOfMonth, startOfMonth } from 'date-fns';
import { Range, toLocalDate } from '../../core/DatePicker';
import { ArrowBack, EventAvailable, InsertChartOutlined, Person, Schedule } from '@material-ui/icons';
import { withScope } from '@/contexts/ScopeContext';
import BlockScheduleConfig from './BlockScheduleConfig';
import { Metrics } from '@/components/pages/blockSchedule/Metrics';

export type BookingStat = {
  id: string;
  title: string;
  unit: string;
  value: number;
};

const Page = styled.div`
  color: ${props => props.theme.textColor.string()};
  padding: 2em 3em;

  ${responsive.md.andSmaller`
    padding: 1em;
  `}
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
`;

const tabs = (match: { url: any }) => [
  {
    id: 'block-schedule',
    name: 'Block Schedule',
    icon: <Schedule />,
    pathname: match.url,
    Component: BlockSchedule,
    exact: true,
  },
  {
    id: 'analytics',
    name: 'Block Analytics',
    icon: <InsertChartOutlined />,
    pathname: `${match.url}/analytics`,
    Component: BookingAnalytics,
  },
  {
    id: 'performance',
    name: 'Physician Analytics',
    icon: <Person />,
    pathname: `${match.url}/performance`,
    Component: PhysicianPerformance,
  },
  {
    id: 'open-blocks',
    name: 'Open Block Analytics',
    icon: <EventAvailable />,
    pathname: `${match.url}/open-blocks`,
    Component: OpenBlockAnalytics,
  },
];

const NarrowHeader = styled(Header)`
  h2 {
    margin: 0.2em 0 0.4em 0;
  }
  margin-bottom: 1em;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-self: start;
  justify-self: flex-end;
  height: 100%;
  justify-content: space-between;
`;

const BlockSchedulePage: any = ({ match, location, isAdmin, isSuperAdmin, isGroupAdmin, organizationId }) => {
  const routes = useMemo(() => tabs(match), [match]);
  const [date, setDate] = useState(new Date());
  const [dateRange, setDateRange] = useState();
  const [physician, setPhysician] = useState();
  const [colorMode, setColorMode] = useState(false);
  const isPowerUser = isSuperAdmin || isGroupAdmin || isAdmin;

  useEffect(() => {
    setDateRange(Range.between(toLocalDate(startOfMonth(date)), toLocalDate(endOfMonth(date))));
  }, [date]);

  const filter = useMemo(
    () => ({
      dateRange: {
        from: toLocalDate(startOfMonth(date)),
        to: toLocalDate(endOfMonth(date)),
      },
      physician,
    }),
    [date, physician]
  );

  const metricsFilter = useMemo(
    () => ({
      dateRange: {
        from: toLocalDate(startOfMonth(date)),
        to: toLocalDate(endOfMonth(date)),
      },
    }),
    [date]
  );
  const statQuery = useSubscription(bookingStats, { variables: { filter: metricsFilter }, skip: !dateRange });

  const stats = useMemo(() => (statQuery?.data?.bookingStats || []) as Array<BookingStat>, [statQuery]);

  const statsMap = useMemo(() => {
    const map = new Map<string, BookingStat>();
    stats.forEach(stat => map.set(stat.id, stat));
    return map;
  }, [stats]);

  const statsLoading = useMemo(() => statQuery.loading || !statsMap.size, [statQuery.loading, statsMap.size]);

  const operationRoomBookings = statsMap.get('operationRoomBookings');
  const scheduledPatients = statsMap.get('scheduledPatients');

  const bookings = operationRoomBookings ? roundValue(operationRoomBookings.value) : 0;
  const utilization = scheduledPatients ? roundValue(scheduledPatients.value) : 0;

  const showMetrics = useMemo(
    () => location.pathname === `${match.url}` || location.pathname === `${match.url}/analytics`,
    [location.pathname, match.url]
  );

  return (
    <Page>
      <NarrowHeader>
        <TitleStack>
          <RouterLink to={isAdmin ? '/' : organizationId ? `/su/${organizationId}` : '/'}>
            <ArrowBack fontSize={'small'} />
            {` Back${isPowerUser ? ' to admin' : ''}`}
          </RouterLink>
          <DefaultTitle>Block Schedule</DefaultTitle>
          <TabNavigation tabs={routes} location={location} />
        </TitleStack>
        <Container>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Switch>
              <Route
                exact
                path={`${match.url}`}
                render={() => (
                  <ActionBar
                    date={date}
                    setDate={setDate}
                    physician={physician}
                    setPhysician={setPhysician}
                    setColorMode={setColorMode}
                    colorMode={colorMode}
                    hidePhysicianFilter={true}
                  />
                )}
              />
              <Route
                path={`${match.url}/analytics`}
                render={() => (
                  <ActionBar
                    date={date}
                    setDate={setDate}
                    physician={physician}
                    setPhysician={setPhysician}
                    colorMode={colorMode}
                    hidePhysicianFilter={true}
                  />
                )}
              />
              <Route
                path={'*'}
                render={() => (
                  <ActionBar
                    date={date}
                    setDate={setDate}
                    physician={physician}
                    setPhysician={setPhysician}
                    colorMode={colorMode}
                    hidePhysicianFilter
                    // disabledDatePicker={!!(location.pathname === `${match.url}/performance` && !!physician)}
                  />
                )}
              />
            </Switch>
            {isPowerUser && <BlockScheduleConfig style={{ marginLeft: 'auto' }} />}
          </div>
        </Container>
      </NarrowHeader>
      {showMetrics && <Metrics statistics={statsMap} loading={statsLoading} date={date} />}
      <Switch>
        {routes.map(({ id, exact, pathname, Component }, index) => (
          <Route
            key={id || pathname || index}
            exact={exact}
            path={pathname}
            render={() => (
              <Component
                date={date}
                physician={physician}
                setPhysician={setPhysician}
                colorMode={colorMode}
                statistics={stats}
                loading={statsLoading}
                filter={filter}
              />
            )}
          />
        ))}
      </Switch>
    </Page>
  );
};

export default compose(withScope, withSession(unpackSessionObject))(BlockSchedulePage);
